import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaQrcode, FaDownload, FaCopy, FaArrowLeft, FaPrint, FaShare } from 'react-icons/fa';
import { workshopAPI } from '../../services/api';
import { useToast } from '../Toast';

const WorkshopQRCode = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [workshop, setWorkshop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [publicLink, setPublicLink] = useState('');

  useEffect(() => {
    fetchWorkshop();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchWorkshop = async () => {
    try {
      setLoading(true);
      const response = await workshopAPI.getById(id);
      setWorkshop(response.data);
      
      // Generate public link
      const link = `${window.location.origin}/workshop/${response.data.public_link_id}`;
      setPublicLink(link);
      
      // Generate QR code URL using a free QR code service
      const qrCodeServiceUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(link)}`;
      setQrCodeUrl(qrCodeServiceUrl);
    } catch (error) {
      console.error('Error fetching workshop:', error);
      showToast('Failed to load workshop details', 'error');
      navigate('/workshops');
    } finally {
      setLoading(false);
    }
  };

  const downloadQRCode = () => {
    if (!qrCodeUrl) return;
    
    const link = document.createElement('a');
    link.href = qrCodeUrl;
    link.download = `workshop-qr-${workshop?.name?.replace(/\s+/g, '-').toLowerCase()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast('QR code downloaded successfully', 'success');
  };

  const copyPublicLink = () => {
    if (!publicLink) return;
    
    navigator.clipboard.writeText(publicLink).then(() => {
      showToast('Public link copied to clipboard!', 'success');
    }).catch(() => {
      showToast('Failed to copy link', 'error');
    });
  };

  const printQRCode = () => {
    if (!qrCodeUrl || !workshop) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Workshop QR Code - ${workshop.name}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              text-align: center; 
              padding: 20px; 
              margin: 0; 
            }
            .qr-container { 
              max-width: 400px; 
              margin: 0 auto; 
              padding: 20px; 
              border: 2px solid #333; 
              border-radius: 10px; 
            }
            .qr-code { 
              max-width: 300px; 
              height: auto; 
              margin: 20px auto; 
            }
            .title { 
              font-size: 24px; 
              font-weight: bold; 
              margin-bottom: 10px; 
              color: #333; 
            }
            .subtitle { 
              font-size: 16px; 
              color: #666; 
              margin-bottom: 20px; 
            }
            .workshop-name {
              font-size: 18px;
              font-weight: bold;
              color: #2563eb;
              margin-bottom: 10px;
            }
            .instructions { 
              font-size: 14px; 
              color: #888; 
              margin-top: 20px; 
            }
            .fees {
              font-size: 16px;
              font-weight: bold;
              color: #059669;
              margin: 10px 0;
            }
            @media print {
              body { margin: 0; }
              .qr-container { border: none; }
            }
          </style>
        </head>
        <body>
          <div class="qr-container">
            <div class="title">Neurons School of Innovation and AI</div>
            <div class="subtitle">Workshop Registration</div>
            <div class="workshop-name">${workshop.name}</div>
            <div class="fees">₹${workshop.fees}</div>
            <img src="${qrCodeUrl}" alt="QR Code" class="qr-code" />
            <div class="instructions">
              Scan this QR code with your phone camera<br>
              to register for this workshop
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const shareQRCode = () => {
    if (navigator.share && publicLink) {
      navigator.share({
        title: `Join ${workshop?.name} - Neurons School`,
        text: `Register for ${workshop?.name} workshop. Scan the QR code or visit the link.`,
        url: publicLink
      }).catch(() => {
        copyPublicLink();
      });
    } else {
      copyPublicLink();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-300">Loading workshop details...</p>
        </div>
      </div>
    );
  }

  if (!workshop) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-300">Workshop not found</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 bg-gray-900 min-h-screen">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(`/workshops/${id}`)}
            className="bg-gray-600 hover:bg-gray-700 text-white p-2 rounded-lg transition-colors shadow-lg"
          >
            <FaArrowLeft />
          </button>
          <h1 className="text-3xl font-bold text-white">Workshop QR Code</h1>
        </div>

        <div className="bg-gray-800 rounded-lg shadow-lg p-8 border border-gray-700">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaQrcode className="text-3xl text-blue-400" />
            </div>
            <h2 className="text-2xl font-semibold text-white mb-2">
              {workshop.name}
            </h2>
            <p className="text-gray-300 mb-2">
              Generate a QR code for easy workshop registration
            </p>
            <div className="text-green-400 font-semibold text-lg">
              ₹{workshop.fees}
            </div>
          </div>

          {qrCodeUrl && (
            <div className="space-y-6">
              {/* QR Code Display */}
              <div className="text-center">
                <div className="bg-gray-700 rounded-lg p-6 inline-block border border-gray-600">
                  <img
                    src={qrCodeUrl}
                    alt="QR Code"
                    className="w-64 h-64 mx-auto"
                  />
                </div>
                <div className="mt-4">
                  <p className="text-sm text-gray-300 mb-2">Public Link:</p>
                  <p className="text-xs text-gray-400 break-all">
                    {publicLink}
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <button
                  onClick={downloadQRCode}
                  className="bg-green-600 hover:bg-green-700 text-white p-3 rounded-lg flex flex-col items-center gap-2 transition-colors"
                >
                  <FaDownload />
                  <span className="text-sm">Download</span>
                </button>
                
                <button
                  onClick={copyPublicLink}
                  className="bg-purple-600 hover:bg-purple-700 text-white p-3 rounded-lg flex flex-col items-center gap-2 transition-colors"
                >
                  <FaCopy />
                  <span className="text-sm">Copy Link</span>
                </button>
                
                <button
                  onClick={printQRCode}
                  className="bg-orange-600 hover:bg-orange-700 text-white p-3 rounded-lg flex flex-col items-center gap-2 transition-colors"
                >
                  <FaPrint />
                  <span className="text-sm">Print</span>
                </button>

                <button
                  onClick={shareQRCode}
                  className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-lg flex flex-col items-center gap-2 transition-colors"
                >
                  <FaShare />
                  <span className="text-sm">Share</span>
                </button>
              </div>

              {/* Instructions */}
              <div className="bg-blue-900 border border-blue-700 rounded-lg p-4">
                <h3 className="font-semibold text-blue-200 mb-2">How to use:</h3>
                <ul className="text-sm text-blue-100 space-y-1">
                  <li>• Display this QR code at your workshop venue or entrance</li>
                  <li>• Participants can scan it with their phone camera</li>
                  <li>• They'll be taken to the workshop registration form</li>
                  <li>• They can complete registration and payment online</li>
                  <li>• Registration data will be automatically recorded</li>
                </ul>
              </div>

              {/* Workshop Details */}
              <div className="bg-gray-700 border border-gray-600 rounded-lg p-4">
                <h3 className="font-semibold text-white mb-2">Workshop Details:</h3>
                <div className="text-sm text-gray-300 space-y-1">
                  <p><strong>Duration:</strong> {new Date(workshop.start_date).toLocaleDateString()} - {new Date(workshop.end_date).toLocaleDateString()}</p>
                  <p><strong>Fees:</strong> ₹{workshop.fees}</p>
                  {workshop.max_participants && (
                    <p><strong>Max Participants:</strong> {workshop.max_participants}</p>
                  )}
                  {workshop.description && (
                    <p><strong>Description:</strong> {workshop.description}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkshopQRCode; 